/* Where the phone is, for the audit screens (`27` section 8): one watch on
 * the browser's location service, shared by every screen. A save takes the
 * position of its moment as the audit's `stood_at`, and a photo its own.
 * Where there is none, nothing that needs one is saved: a position filled in
 * later, from an older fix or a pin, would be a guess at where they stood.
 *
 * Not PreCapture's position, which its drive screens keep with a heading
 * (FieldApp.tsx), and not a simulation.
 */
import { useSyncExternalStore } from "react";

import type { Fix } from "./unsyncedWork";

/* A position no older than this is where the phone is now. Long enough that a
 * phone standing still, which the location service reports less often, still
 * has one; short enough that an arborist who has walked to the next tree does
 * not save the last tree's. */
export const FRESH_MS = 30_000;

/* How long a first position may take before the screens stop saying one is
 * being found and say the signal is weak. */
export const SEARCH_MS = 15_000;

export const DENIED =
  "Audits need your location, and this phone is not sharing it. Turn on location for Arborcision in your phone's settings.";
export const UNSUPPORTED = "Audits need your location, and this browser cannot share it.";
export const WEAK = "GPS signal weak. Try a different location.";
export const FINDING = "Finding your position…";

export interface Located {
  /* The newest position the phone gave, and when it arrived. */
  last: { fix: Fix; at: number } | null;
  /* Why the phone gives none at all: location refused, or no location
   * service in this browser. */
  refused: "denied" | "unsupported" | null;
  /* When the watch began, so a first position on its way reads as one being
   * found rather than as no signal. */
  since: number;
  /* The location service said it has no position, since the last it gave. */
  lost: boolean;
}

/* GeolocationPositionError.PERMISSION_DENIED, by value: the error a test
 * raises is a plain object, which carries no such constant. */
const PERMISSION_DENIED = 1;

export function toFix(position: GeolocationPosition): Fix {
  const { latitude, longitude, accuracy } = position.coords;
  return { lat: latitude, lng: longitude, accuracyM: Number.isFinite(accuracy) ? accuracy : null };
}

/* The phone's position now, or null where it has none recent enough to say
 * where the arborist is standing. */
export function positionNow(located: Located, now: number): Fix | null {
  if (located.refused || !located.last || now - located.last.at > FRESH_MS) return null;
  return located.last.fix;
}

/* Why nothing that needs a position can be saved now, or null where it can. */
export function noPosition(located: Located, now: number): string | null {
  if (located.refused === "denied") return DENIED;
  if (located.refused === "unsupported") return UNSUPPORTED;
  if (positionNow(located, now)) return null;
  return located.last || located.lost || now - located.since > SEARCH_MS ? WEAK : FINDING;
}

/* Where a save made now records the arborist as standing, or why it cannot be
 * made. A save takes its position only from here, so none is made without one. */
export function whereStood(located: Located, now: number): { fix: Fix } | { why: string } {
  const fix = positionNow(located, now);
  return fix ? { fix } : { why: noPosition(located, now) ?? WEAK };
}

/* Where a photo was taken: the phone's position, for a photo taken just now.
 * One taken earlier - chosen from the phone's library rather than the camera -
 * was taken somewhere this page never saw, so it has none. */
export function shotLocation(takenAt: number, located: Located, now: number): Fix | null {
  return now - takenAt > FRESH_MS ? null : positionNow(located, now);
}

/* The page, as far as the watch needs it: coming back to the app is when
 * location turned on in the phone's settings can take effect. */
type Page = Pick<Document, "visibilityState" | "addEventListener" | "removeEventListener">;

/* One watch for every subscriber: started by the first, stopped by the last.
 * Exported for its test, which gives it a fake location service and page. */
export function positionSource(geo: Geolocation | undefined, page: Page | undefined, clock: () => number = Date.now) {
  let located: Located = { last: null, refused: null, since: clock(), lost: false };
  const listeners = new Set<() => void>();
  let watch: number | null = null;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const emit = (next: Located) => {
    located = next;
    for (const listener of listeners) listener();
  };
  // What the screens say can change with no event from the location service -
  // a position ageing out, or a first one taking too long to arrive - so they
  // are told when it does.
  const wake = () => {
    clearTimeout(timer);
    const due = located.last ? located.last.at + FRESH_MS : located.since + SEARCH_MS;
    const wait = due + 1 - clock();
    if (wait > 0) timer = setTimeout(() => emit({ ...located }), wait);
  };
  const stop = () => {
    if (watch !== null) geo?.clearWatch(watch);
    watch = null;
    clearTimeout(timer);
  };
  const start = () => {
    if (!geo) return emit({ ...located, refused: "unsupported" });
    // A watch starts from no position, asking again after a refusal as at
    // first: one from before location was refused is not where the phone is
    // now, and a new one on its way reads as being found.
    emit({ last: null, refused: null, since: clock(), lost: false });
    wake();
    watch = geo.watchPosition(
      (position) => {
        emit({ ...located, last: { fix: toFix(position), at: clock() }, lost: false });
        wake();
      },
      (error) => {
        if (error.code !== PERMISSION_DENIED) return emit({ ...located, lost: true });
        stop();
        emit({ ...located, refused: "denied" });
      },
      { enableHighAccuracy: true, maximumAge: 0 },
    );
  };
  const back = () => {
    if (page?.visibilityState === "visible" && located.refused === "denied") start();
  };

  return {
    subscribe(listener: () => void): () => void {
      listeners.add(listener);
      if (listeners.size === 1) {
        start();
        page?.addEventListener("visibilitychange", back);
      }
      return () => {
        listeners.delete(listener);
        if (listeners.size > 0) return;
        stop();
        page?.removeEventListener("visibilitychange", back);
      };
    },
    snapshot: (): Located => located,
  };
}

let shared: ReturnType<typeof positionSource> | null = null;

/* The phone's position as the audit screens read it. The first screen to
 * read it starts the watch, which is when the phone asks to share location. */
export function usePosition(): Located {
  shared ??= positionSource("geolocation" in navigator ? navigator.geolocation : undefined, document);
  return useSyncExternalStore(shared.subscribe, shared.snapshot);
}
